export type LinkifySegment =
  | { type: 'text'; value: string }
  | { type: 'link'; value: string; href: string };

// Matches http(s):// URLs and bare www. hosts, stopping at whitespace or '<'
const URL_REGEX = /\b(?:https?:\/\/|www\.)[^\s<]+/gi;

const TRAILING_PUNCTUATION = /[.,;:!?'"]$/;

const BRACKET_PAIRS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

function countChar(str: string, char: string): number {
  return str.split(char).length - 1;
}

/**
 * Strip punctuation that most likely belongs to the surrounding sentence
 * rather than the URL, e.g. "see https://a.com." or "(https://a.com)".
 * Closing brackets are kept when they balance an opening one inside the URL,
 * so links like https://en.wikipedia.org/wiki/Foo_(bar) survive intact.
 */
function trimTrailing(url: string): string {
  let result = url;
  for (;;) {
    const last = result[result.length - 1];
    if (TRAILING_PUNCTUATION.test(last)) {
      result = result.slice(0, -1);
      continue;
    }
    const open = BRACKET_PAIRS[last];
    if (open && countChar(result, last) > countChar(result, open)) {
      result = result.slice(0, -1);
      continue;
    }
    return result;
  }
}

function toHref(url: string): string | null {
  const candidate = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Split text into plain-text and link segments. Only http/https URLs
 * (including bare "www." hosts) become links.
 */
export function linkify(text: string): LinkifySegment[] {
  const segments: LinkifySegment[] = [];
  let lastIndex = 0;

  const pushText = (value: string) => {
    if (!value) return;
    const prev = segments[segments.length - 1];
    if (prev?.type === 'text') {
      prev.value += value;
    } else {
      segments.push({ type: 'text', value });
    }
  };

  for (const match of text.matchAll(URL_REGEX)) {
    const start = match.index ?? 0;
    const url = trimTrailing(match[0]);
    const href = url.length > 'www.'.length ? toHref(url) : null;
    if (!href) continue;

    pushText(text.slice(lastIndex, start));
    segments.push({ type: 'link', value: url, href });
    lastIndex = start + url.length;
  }

  pushText(text.slice(lastIndex));
  return segments;
}
